import { useRef, useCallback, useEffect, useState } from "react";

function useSwipeScroll(
  scrollContainerRef: React.RefObject<HTMLDivElement | null>,
  wrapperRef: React.RefObject<HTMLDivElement | null>,
) {
  const isHorizontalScrollListenerSetRef = useRef(false);
  const [isScrolledLeft, setIsScrollLeft] = useState<boolean>(false);
  const [startingScrollPosition, setStartingScrollPosition] = useState(0);

  const snapWrapperBack = useCallback(() => {
    // This function helps us automatically snap back the swiped card
    // if the user decides to scroll the whole page down
    if (wrapperRef?.current) {
      wrapperRef?.current?.scrollTo({ left: startingScrollPosition });
    }
  }, [wrapperRef, startingScrollPosition]);

  const handleSwipeScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (
        event.currentTarget.scrollLeft === startingScrollPosition &&
        isHorizontalScrollListenerSetRef.current
      ) {
        scrollContainerRef.current?.removeEventListener("scroll", snapWrapperBack);
        isHorizontalScrollListenerSetRef.current = false;
        setIsScrollLeft(false);
      } else if (
        event.currentTarget.scrollLeft !== startingScrollPosition &&
        !isHorizontalScrollListenerSetRef.current
      ) {
        setIsScrollLeft(true);
        scrollContainerRef.current?.addEventListener("scroll", snapWrapperBack);
        isHorizontalScrollListenerSetRef.current = true;
      }
    },
    [scrollContainerRef, snapWrapperBack, startingScrollPosition],
  );

  useEffect(() => {
    const mainComponent = wrapperRef?.current?.querySelector(
      '[data-swipe-scroll-id="main-component"]',
    ) as HTMLDivElement;
    if (mainComponent) {
      setStartingScrollPosition(mainComponent.offsetLeft);
    }
  }, [wrapperRef]);

  useEffect(() => {
    if (startingScrollPosition > 0 && wrapperRef?.current) {
      wrapperRef?.current?.scrollTo({ left: startingScrollPosition });
    }
  }, [wrapperRef, startingScrollPosition]);

  useEffect(() => {
    const scrollContainerRefAtTheTime = scrollContainerRef.current;
    return () => {
      // Clean up to make sure the listener is removed when component unmounts
      scrollContainerRefAtTheTime?.removeEventListener("scroll", snapWrapperBack);
    };
  }, [snapWrapperBack, scrollContainerRef]);

  return {
    scrollContainerRef,
    handleSwipeScroll,
    snapWrapperBack,
    isScrolledLeft,
  };
}

export default useSwipeScroll;
